/**
 * Location Service for Regional Search
 * Handles API calls for German zip code data and federal states
 */

import { apiClient } from './apiService'

const locationService = {
  /**
   * Import zip codes from de-zip-codes.txt file
   * @returns {Promise<Object>} Import statistics
   */
  importZipCodes: async () => {
    try {
      const response = await apiClient.post('/locations/import-zip-codes')
      return response.data
    } catch (error) {
      console.error('Failed to import zip codes:', error)
      throw error
    }
  },

  /**
   * Get import statistics
   * @returns {Promise<Object>} Import stats
   */
  getImportStats: async () => {
    try {
      const response = await apiClient.get('/locations/import-stats')
      return response.data
    } catch (error) {
      console.error('Failed to get import stats:', error)
      throw error
    }
  },

  /**
   * Get all federal states
   * @returns {Promise<Array>} List of federal states with codes
   */
  getFederalStates: async () => {
    try {
      const response = await apiClient.get('/locations/states')
      return response.data
    } catch (error) {
      console.error('Failed to get federal states:', error)
      throw error
    }
  },

  /**
   * Get cities for a specific federal state
   * @param {string} stateCode - State code (BY, BB, etc.)
   * @param {number} limit - Maximum number of cities (null for all)
   * @param {string} cityType - 'large', 'small', or 'mixed'
   * @returns {Promise<Array>} List of cities
   */
  getCitiesByState: async (stateCode, limit = null, cityType = 'mixed') => {
    try {
      const params = { city_type: cityType }
      if (limit) params.limit = limit
      
      const response = await apiClient.get(`/locations/states/${stateCode}/cities`, { params })
      return response.data
    } catch (error) {
      console.error(`Failed to get cities for state ${stateCode}:`, error)
      throw error
    }
  },

  /**
   * Search cities by name
   * @param {string} query - Search query
   * @param {string} stateCode - Optional state filter
   * @param {number} limit - Maximum results
   * @returns {Promise<Array>} List of matching cities
   */
  searchCities: async (query, stateCode = null, limit = 20) => {
    try {
      const params = { query, limit }
      if (stateCode) params.state_code = stateCode
      
      const response = await apiClient.get('/locations/cities/search', { params })
      return response.data
    } catch (error) {
      console.error('Failed to search cities:', error)
      throw error
    }
  },

  /**
   * Execute regional search with extraction
   * @param {Object} config - Regional search configuration
   * @returns {Promise<Object>} Extraction results
   */
  executeRegionalSearch: async (config) => {
    try {
      const response = await apiClient.post('/data/extract-regional', config)
      return response.data
    } catch (error) {
      console.error('Failed to execute regional search:', error)
      throw error
    }
  }
}

export default locationService
